/**
 * assets/cart-drawer-recommendations.js
 *
 * Fetches complementary-product recommendations for the most recently
 * added item and injects them into a placeholder container inside the
 * cart drawer. Wires the size dropdown + "Add to cart" button to POST
 * to /cart/add.js, then lets Horizon's own cart:update event system
 * refresh everything else (line items, cart count, etc).
 *
 * Requires:
 * 1. A container in your cart drawer markup, e.g.:
 *      <div id="CartDrawerRecommendations"></div>
 *    Drop it inside cart-drawer-component's dialog markup, wherever you
 *    want the module to appear (commonly just above the cart summary).
 *
 * 2. sections/cart-drawer-recommendations.liquid (companion file).
 *
 * 3. Complementary products configured per-product in Shopify admin
 *    (Search & Discovery app > product > "Complementary products").
 *
 * 4. This file loaded as a module in theme.liquid, e.g.:
 *      <script src="{{ 'cart-drawer-recommendations.js' | asset_url }}" type="module"></script>
 *    Horizon's import map (already set up by the theme) makes
 *    "@theme/events" resolve correctly.
 */

import { ThemeEvents, CartAddEvent } from '@theme/events';

const RECS_SECTION_ID = 'cart-drawer-recommendations';
const RECS_CONTAINER_SELECTOR = '#CartDrawerRecommendations';
const PRODUCTS_TO_SHOW = 2;

async function renderCartDrawerRecommendations(productId) {
  const container = document.querySelector(RECS_CONTAINER_SELECTOR);
  if (!container || !productId) return;

  try {
    const url = `/recommendations/products?section_id=${RECS_SECTION_ID}&product_id=${productId}&intent=complementary&limit=${PRODUCTS_TO_SHOW}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const recs = doc.querySelector('[data-cart-drawer-recommendations]');

    container.innerHTML = recs ? recs.outerHTML : '';
    bindRecommendationEvents(container);
  } catch (error) {
    console.error('[cart-drawer-recommendations]', error);
    container.innerHTML = '';
  }
}

function bindRecommendationEvents(container) {
  container.querySelectorAll('[data-cart-recs-add]').forEach((button) => {
    button.addEventListener('click', onAddClick);
  });

  container.querySelectorAll('[data-recs-variant-select]').forEach((select) => {
    select.addEventListener('change', onVariantChange);
  });
}

// Keep the Add button + displayed price in sync with the chosen size.
function onVariantChange(event) {
  const select = event.currentTarget;
  const item = select.closest('.cart-drawer-recs__item');
  const option = select.selectedOptions[0];
  if (!item || !option) return;

  const button = item.querySelector('[data-cart-recs-add]');
  const priceEl = item.querySelector('[data-recs-price]');

  if (button) {
    button.setAttribute('data-variant-id', option.value);
    button.disabled = option.disabled;
    button.textContent = option.disabled
      ? (window.Theme?.translations?.sold_out ?? 'Sold out')
      : (window.Theme?.translations?.add_to_cart ?? 'Add to cart');
  }

  if (priceEl && option.dataset.price) {
    priceEl.textContent = option.dataset.price;
  }
}

// Same section-collection pattern Horizon's own product-form.js uses,
// so the cart line items morph/refresh automatically after our add.
function getCartSectionIds() {
  const ids = [];
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.push(el.dataset.sectionId);
  });
  return ids.join(',');
}

async function onAddClick(event) {
  const button = event.currentTarget;
  const variantId = button.getAttribute('data-variant-id');
  if (!variantId || button.disabled) return;

  button.disabled = true;
  button.classList.add('is-loading');

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: getCartSectionIds(),
      }),
    });

    const result = await response.json();

    if (result.status) {
      throw new Error(result.message || `Add to cart failed: ${response.status}`);
    }

    // Dispatch the same event Horizon's own product forms dispatch so
    // cart-drawer-component (auto-open, count announcement),
    // cart-items-component (line item refresh via result.sections), and
    // the header cart bubble all update themselves.
    document.documentElement.dispatchEvent(
      new CartAddEvent(result, button.id || 'cart-drawer-recommendations', {
        source: 'cart-drawer-recommendations',
        itemCount: 1,
        productId: variantId,
        sections: result.sections,
      })
    );
  } catch (error) {
    console.error('[cart-drawer-recommendations] add to cart failed', error);
  } finally {
    button.disabled = false;
    button.classList.remove('is-loading');
  }
}

// Re-run recommendations after every successful cart change, using the
// productId Horizon already includes on the event detail.
document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (event.detail?.data?.didError) return;
  const productId = event.detail?.data?.productId;
  if (productId) renderCartDrawerRecommendations(productId);
});

export { renderCartDrawerRecommendations };
